import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentActivityLog = ({ activities, onViewDetails, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const activityTypes = [
    { value: 'all', label: 'Todas las Actividades' },
    { value: 'adjustment', label: 'Ajustes de Stock' },
    { value: 'withdrawal', label: 'Retiradas' },
    { value: 'receipt', label: 'Recepciones' },
    { value: 'transfer', label: 'Transferencias' }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'adjustment':
        return { name: 'Edit', color: 'text-primary' };
      case 'withdrawal':
        return { name: 'Minus', color: 'text-error' };
      case 'receipt':
        return { name: 'Plus', color: 'text-success' };
      case 'transfer':
        return { name: 'ArrowRightLeft', color: 'text-warning' };
      default:
        return { name: 'Activity', color: 'text-text-muted' };
    }
  };

  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'adjustment':
        return 'Ajuste';
      case 'withdrawal':
        return 'Retirada';
      case 'receipt':
        return 'Recepción';
      case 'transfer':
        return 'Transferencia';
      default:
        return 'Actividad';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Hace ${Math.floor(diffInMinutes / 60)} h`;
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredActivities = activities.filter(activity => 
    filter === 'all' || activity.type === filter
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border bg-secondary-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Actividad Reciente
          </h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            onClick={handleRefresh}
            loading={isRefreshing}
          />
        </div>
      </div>

      {/* Filter */}
      <div className="px-6 py-3 border-b border-border">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          {activityTypes.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Activity List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredActivities.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <Icon name="Activity" size={32} className="text-text-muted mx-auto mb-2" />
            <p className="text-sm text-text-muted">
              {filter === 'all' ? 'No hay actividad reciente' : 'No hay actividades de este tipo'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredActivities.map((activity) => {
              const iconConfig = getActivityIcon(activity.type);
              
              return (
                <div
                  key={activity.id}
                  className="px-6 py-4 hover:bg-secondary-50 transition-micro cursor-pointer"
                  onClick={() => onViewDetails(activity)}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className={`mt-1 ${iconConfig.color}`}>
                      <Icon name={iconConfig.name} size={16} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-body-medium text-text-primary">
                            {getActivityTypeLabel(activity.type)}
                          </span>
                          <span className="text-xs px-2 py-1 bg-secondary-100 text-text-secondary rounded-full">
                            {activity.productName}
                          </span>
                        </div>
                        <span className="text-xs text-text-muted">
                          {formatTimestamp(activity.timestamp)}
                        </span>
                      </div>

                      <p className="text-sm text-text-secondary mt-1">
                        {activity.description}
                      </p>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-4 text-xs text-text-muted">
                          <span>Por: {activity.employeeName}</span>
                          {activity.quantity && (
                            <span>
                              Cantidad: {Math.abs(activity.quantity).toLocaleString('es-ES')}
                            </span>
                          )}
                        </div>
                        
                        {activity.quantity && (
                          <div className={`text-sm font-body-medium ${
                            activity.quantity > 0 ? 'text-success' : 'text-error'
                          }`}>
                            {activity.quantity > 0 ? '+' : ''}{activity.quantity.toLocaleString('es-ES')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredActivities.length > 0 && (
        <div className="px-6 py-3 border-t border-border bg-secondary-50">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => console.log('Navigate to full activity log')}
          >
            Ver Historial Completo
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecentActivityLog;